import type { ChatType } from '@form-factory/schema';
import {
  TonePreset,
  ResponseLengthPreset,
  Gender,
  EFitnessProvider,
} from '@form-factory/schema/enums';
import { AgentDefaults } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import { useFormContext } from '@utima/ui-informed';

import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { useEnumTranslations } from '@/hooks';
import { useAccess } from '@/hooks/useAccess';

export function BasicConfigFields() {
  const { hasAccess } = useAccess();
  const { t } = useLingui();
  const { initialValues } = useFormContext<ChatType>();
  const translations = useEnumTranslations();

  const { schema: detailsSchema } = useFieldsSchema(
    [
      {
        name: 'name',
        label: t`Name`,
        defaultValue: AgentDefaults.name,
        required: true,
      },
      {
        name: 'chatbotConfig.efitnessProvider',
        label: t`E-fitness provider`,
        type: 'select',
        options: translations.efitnessProvider,
        required: true,
        defaultValue: EFitnessProvider.Czechia,
      },
      {
        name: 'agentConfig.gender',
        label: t`Gender (Beta)`,
        type: 'select',
        options: translations.gender,
        defaultValue: Gender.None,
        tooltip: t`AI will use this settings to personalize the agent's response. This is currently in beta and may introduce some side effects with the agent's response.`,
        showOptional: false,
      },
      {
        name: 'agentConfig.personalization',
        label: t`Personalization`,
        type: 'text',
        uiProps: {
          rows: 5,
        },
        uiControl: 'textarea',
        required: true,
        defaultValue: t`Answering questions about the company, their products and services to provide users with sufficient information to solve problems`,
        tooltip: t`Used this field to personalize your agent from further information about your business, products or additional directives. This helps your agent to better understand your business and provide better answers to users for generic questions about your business.`,
      },
      {
        name: 'agentConfig.tonePreset',
        label: t`Tone`,
        type: 'select',
        options: translations.tonePreset,
        defaultValue: TonePreset.Casual,
        showOptional: false,
      },
      {
        name: 'agentConfig.responseLengthPreset',
        label: t`Response length`,
        type: 'select',
        options: translations.responseLengthPreset,
        defaultValue: ResponseLengthPreset.Short,
        showOptional: false,
      },
      {
        name: 'agentConfig.language',
        label: t`Language`,
        type: 'select',
        options: {
          automatic: t`Automatic`,
          ...translations.supportedLanguages,
        },
        defaultValue: 'cs',
        showOptional: false,
      },
      {
        name: 'agentConfig.showSources',
        label: t`Show sources`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: true,
        required: true,
      },
      {
        name: 'agentConfig.showFollowUpQuestions',
        label: t`Show follow-up questions`,
        type: 'checkbox',
        uiControl: 'switch',
        tooltip: t`Disabling this option will hide follow-up questions.`,
        defaultValue: true,
        required: true,
      },
      {
        name: 'agentConfig.imageSupport',
        label: t`Image support`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: true,
        showOptional: false,
      },
    ],
    [t, hasAccess, initialValues],
  );

  return (
    <Card title={t`Basic details`}>
      <SchemaFields schema={detailsSchema} />
    </Card>
  );
}
